import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  FormHelperText,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Build,
  Download,
  Computer,
  Terminal,
  AccessTime,
  Refresh,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { Customer } from '../../types/exporter';
import { ScrapeTarget } from '../../../../../api/cansense/installerApi';

// ============================================================================
// Types
// ============================================================================

export interface InstallerFormData {
  osType: 'windows' | 'linux';
  customerId: number;
  deviceName: string;
  expiryDays: number;
  maxUses: number;
  scrapeTargets?: ScrapeTarget[];
}

interface GeneratedInstaller {
  token: string;
  downloadUrl: string;
  expiresAt: string;
  maxUses: number;
}

interface InstallerGeneratorDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (data: InstallerFormData) => Promise<GeneratedInstaller | null>;
  customers?: Customer[];
}

// ============================================================================
// Constants
// ============================================================================

const EXPIRY_OPTIONS = [
  { value: 1, label: '1 day' },
  { value: 3, label: '3 days' },
  { value: 7, label: '1 week' },
  { value: 14, label: '2 weeks' },
  { value: 30, label: '1 month' }
];

const MAX_USES_OPTIONS = [
  { value: 1, label: '1 use (Single device)' },
  { value: 5, label: '5 uses' },
  { value: 10, label: '10 uses' },
  { value: -1, label: 'Unlimited' }
];

const SCRAPE_TARGET_PRESETS: Array<{ label: string; jobName: string; port: number }> = [
  { label: 'SQL Exporter', jobName: 'sql_exporter', port: 9399 },
  { label: 'Redis Exporter', jobName: 'redis_exporter', port: 9121 },
  { label: 'IIS Exporter', jobName: 'iis_exporter', port: 9113 },
  { label: 'MSSQL Exporter', jobName: 'mssql_exporter', port: 4000 },
  { label: 'Blackbox Exporter', jobName: 'blackbox_exporter', port: 9115 },
];

// ============================================================================
// Component
// ============================================================================

export const InstallerGeneratorDialog: React.FC<InstallerGeneratorDialogProps> = ({
  open,
  onClose,
  onGenerate,
  customers = []
}) => {
  // Form state
  const [osType, setOsType] = useState<'windows' | 'linux'>('windows');
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [deviceName, setDeviceName] = useState('');
  const [expiryDays, setExpiryDays] = useState(7);
  const [maxUses, setMaxUses] = useState(1);
  const [scrapeTargets, setScrapeTargets] = useState<ScrapeTarget[]>([]);

  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generatedInstaller, setGeneratedInstaller] = useState<GeneratedInstaller | null>(null);

  // Reset form
  const resetForm = () => {
    setOsType('windows');
    setCustomerId('');
    setDeviceName('');
    setExpiryDays(7);
    setMaxUses(1);
    setScrapeTargets([]);
    setGeneratedInstaller(null);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Handle OS type change
  const handleOsTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newOsType: 'windows' | 'linux' | null
  ) => {
    if (newOsType !== null) {
      setOsType(newOsType);
      setGeneratedInstaller(null);
      if (newOsType === 'linux') {
        setScrapeTargets([]);
      }
    }
  };

  // Scrape target helpers
  const addScrapeTarget = () => {
    setScrapeTargets(prev => [...prev, { jobName: '', port: 0 }]);
  };

  const removeScrapeTarget = (index: number) => {
    setScrapeTargets(prev => prev.filter((_, i) => i !== index));
  };

  const updateScrapeTarget = (index: number, field: keyof ScrapeTarget, value: string | number) => {
    setScrapeTargets(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  const addPreset = (preset: typeof SCRAPE_TARGET_PRESETS[0]) => {
    if (!scrapeTargets.some(t => t.jobName === preset.jobName)) {
      setScrapeTargets(prev => [...prev, { jobName: preset.jobName, port: preset.port }]);
    }
  };

  // Generate installer
  const handleGenerate = async () => {
    if (!customerId) {
      setError('Please select a customer');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedInstaller(null);

    try {
      const formData: InstallerFormData = {
        osType,
        customerId: customerId as number,
        deviceName,
        expiryDays,
        maxUses,
        scrapeTargets: scrapeTargets.filter(t => t.jobName && t.port > 0),
      };

      const result = await onGenerate(formData);
      if (result) {
        setGeneratedInstaller(result);
      } else {
        setError('Failed to generate installer');
      }
    } catch (err) {
      setError('Failed to generate installer');
      console.error('Generate installer failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  // Check if form is valid
  const isFormValid = customerId !== '';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '500px' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Build sx={{ mr: 1 }} />
          Generate Exporter Installer
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {isGenerating ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              gap: 3
            }}>
              <CircularProgress size={48} />
              <Typography variant="h6">
                Building {osType === 'windows' ? 'Windows' : 'Linux'} Installer
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Packaging installer for{' '}
                {customers.find(c => c.cust_id === customerId)?.cust_name || 'customer'}
                {deviceName ? ` - ${deviceName}` : ''}
              </Typography>
              <Box sx={{ width: '60%', mt: 1 }}>
                <LinearProgress />
              </Box>
              <Typography variant="caption" color="text.secondary">
                This may take up to 30 seconds
              </Typography>
            </Box>
          ) : !generatedInstaller ? (
            <>
              {/* OS Selection */}
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                Operating System
              </Typography>

              <ToggleButtonGroup
                value={osType}
                exclusive
                onChange={handleOsTypeChange}
                sx={{ mb: 3, width: '100%' }}
              >
                <ToggleButton
                  value="windows"
                  sx={{
                    flex: 1,
                    py: 2,
                    flexDirection: 'column',
                    gap: 0.5,
                    textTransform: 'none'
                  }}
                >
                  <Computer />
                  <Typography variant="body2" fontWeight="bold">
                    Windows
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Windows Exporter (.zip installer)
                  </Typography>
                </ToggleButton>
                <ToggleButton
                  value="linux"
                  sx={{
                    flex: 1,
                    py: 2,
                    flexDirection: 'column',
                    gap: 0.5,
                    textTransform: 'none'
                  }}
                >
                  <Terminal />
                  <Typography variant="body2" fontWeight="bold">
                    Linux
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Node Exporter (shell script)
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>

              <Divider sx={{ my: 3 }} />

              {/* Configuration */}
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                Configuration
              </Typography>

              <FormControl fullWidth sx={{ mb: 3 }} error={!!error && !customerId}>
                <InputLabel>Customer *</InputLabel>
                <Select
                  value={customerId}
                  onChange={(e) => {
                    setCustomerId(e.target.value as number);
                    setError('');
                  }}
                  label="Customer *"
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.cust_id} value={customer.cust_id}>
                      {customer.cust_name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {error && !customerId ? error : 'Select the customer this device belongs to'}
                </FormHelperText>
              </FormControl>

              <TextField
                fullWidth
                label="Device Name"
                placeholder="e.g., web-server-01"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                helperText="Optional - helps identify the device after installation"
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Token Expiry</InputLabel>
                  <Select
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(e.target.value as number)}
                    label="Token Expiry"
                  >
                    {EXPIRY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AccessTime sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>How long the installer token is valid</FormHelperText>
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Max Uses</InputLabel>
                  <Select
                    value={maxUses}
                    onChange={(e) => setMaxUses(e.target.value as number)}
                    label="Max Uses"
                  >
                    {MAX_USES_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>How many times the installer can be used</FormHelperText>
                </FormControl>
              </Box>

              {/* Info about what the installer does */}
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>The installer will:</strong>
                  <br />
                  • Download and install {osType === 'windows' ? 'Windows Exporter' : 'Node Exporter'}
                  <br />
                  • Configure it to run as a system service
                  <br />
                  • Register the device with CANsense monitoring
                  <br />
                  • Begin collecting metrics automatically
                </Typography>
              </Alert>

              {/* Custom Scrape Targets (Windows only) */}
              {osType === 'windows' && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Custom Scrape Targets
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Configure additional local exporters for Alloy to scrape. Targets always scrape localhost only.
                  </Typography>

                  {/* Preset buttons */}
                  <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {SCRAPE_TARGET_PRESETS.map(preset => (
                      <Chip
                        key={preset.jobName}
                        label={preset.label}
                        size="small"
                        variant={scrapeTargets.some(t => t.jobName === preset.jobName) ? 'filled' : 'outlined'}
                        color={scrapeTargets.some(t => t.jobName === preset.jobName) ? 'primary' : 'default'}
                        onClick={() => addPreset(preset)}
                        disabled={scrapeTargets.some(t => t.jobName === preset.jobName)}
                      />
                    ))}
                  </Box>

                  {/* Target rows */}
                  {scrapeTargets.map((target, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        label="Job Name"
                        value={target.jobName}
                        onChange={e => updateScrapeTarget(index, 'jobName', e.target.value)}
                        sx={{ flex: 2 }}
                      />
                      <TextField
                        size="small"
                        label="Port"
                        type="number"
                        value={target.port || ''}
                        onChange={e => updateScrapeTarget(index, 'port', parseInt(e.target.value) || 0)}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        label="Path"
                        value={target.metricsPath || '/metrics'}
                        onChange={e => updateScrapeTarget(index, 'metricsPath', e.target.value)}
                        sx={{ flex: 1.5 }}
                      />
                      <IconButton size="small" onClick={() => removeScrapeTarget(index)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addScrapeTarget}
                    sx={{ mt: 1 }}
                  >
                    Add Target
                  </Button>
                </Box>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          ) : (
            <>
              {/* Generated Installer Result */}
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Installer generated successfully!</strong>
                  <br />
                  Expires: {new Date(generatedInstaller.expiresAt).toLocaleString()}
                  {generatedInstaller.maxUses > 0 && ` • Max uses: ${generatedInstaller.maxUses}`}
                </Typography>
              </Alert>

              {/* Download */}
              <Button
                variant="contained"
                startIcon={<Download />}
                href={generatedInstaller.downloadUrl}
                target="_blank"
                fullWidth
                size="large"
              >
                Download {osType === 'windows' ? 'Windows' : 'Linux'} Installer
              </Button>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        {isGenerating ? (
          <Button onClick={handleClose} disabled>
            Cancel
          </Button>
        ) : !generatedInstaller ? (
          <>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              variant="contained"
              disabled={!isFormValid}
              startIcon={<Build />}
            >
              Generate Installer
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={resetForm}
              startIcon={<Refresh />}
            >
              Generate Another
            </Button>
            <Button onClick={handleClose} variant="contained">
              Done
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InstallerGeneratorDialog;
